import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { BackdropLoader, toast } from '@livable/saas-react-components';
import { Button, Chip, Paper, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import moment from 'moment';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentMethod } from 'src/components/settings/PaymentMethod';
import DefaultSubscriptionUnit from 'src/components/unit/DefaultSubscriptionUnit';
import { AMOUNT_TYPE } from 'src/constants/payments';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { useStore } from 'src/stores';
import { dateToFormat } from 'src/utils/date';
import { couponInitialValue, couponSchema } from 'src/utils/formValidation/Payment';
// import ChangePaymentPeriodModal from './ChangePaymentPeriodModal';
// import CancelSubscriptionModal from './CancelSubscriptionModal';
import useStyles from './styles';
export const SubscriptionTab = observer(() => {
  var _store$payment$autopa, _store$payment$autopa2;
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isLoading = _useState2[0],
    setIsLoading = _useState2[1];
  const subscriptions = store.stripe.subscriptionCompanySubscriptions;
  const activeAccountSubscription = [...subscriptions].filter(subscription => {
    var _subscription$scope, _subscription$status;
    return ((_subscription$scope = subscription.scope) === null || _subscription$scope === void 0 ? void 0 : _subscription$scope.toLowerCase()) === 'account' && ((_subscription$status = subscription.status) === null || _subscription$status === void 0 ? void 0 : _subscription$status.toLowerCase()) === 'active';
  }).sort((a, b) => moment(b.startDate).valueOf() - moment(a.startDate).valueOf())[0];
  const getAutopayMethod = async () => {
    const resp = await store.payment.getAutopayMethodAsync.run();
    return resp;
  };
  const getCompanyUnits = async () => {
    const companyId = localStorage.getItem(USER_SETTINGS_KEYS.company) || '';
    const resp = await store.company.getCompanyUnitsAsync.run({
      companyId
    });
    return resp;
  };
  const getCoupons = async () => {
    const resp = await store.payment.getCouponsAsync.run();
    return resp;
  };
  const getPaymentSummary = async () => {
    const resp = await store.payment.getPaymentSummaryAsync.run();
    return resp;
  };
  const getSubscriptionAsync = async () => {
    const resp = await store.payment.getSubscriptionAsync.run();
    return resp;
  };
  const getUsersPaperUsageAsync = async () => {
    const resp = await store.payment.getUsersPaperUsageAsync.run();
    return resp;
  };
  const getSubscriptionCompany = async () => {
    const resp = await store.stripe.getSubscriptionCompanyAsync.run();
    return resp;
  };
  const fetchData = () => {
    setIsLoading(true);
    return Promise.all([getAutopayMethod(), getCompanyUnits(), getCoupons(), getPaymentSummary(), getSubscriptionAsync(), getUsersPaperUsageAsync(), getSubscriptionCompany()]).then(() => setIsLoading(false));
  };
  const formik = useFormik({
    initialValues: couponInitialValue,
    validationSchema: couponSchema,
    onSubmit: async values => {
      const data = {
        stripeCouponId: values.coupon.trim()
      };
      await store.payment.applyCouponAsync.run(data).then(resp => {
        if (resp && resp.isSuccessful) {
          toast('success', {
            body: ['Coupon is applied']
          }); // ? i18n
          return fetchData();
        }
      });
    }
  });
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.pageWrapper
  }, /*#__PURE__*/React.createElement(Paper, {
    variant: "evenWrapper"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.subscriptionOverviewHeader
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('subscriptions.subscription_overview')), (activeAccountSubscription === null || activeAccountSubscription === void 0 ? void 0 : activeAccountSubscription.startDate) && /*#__PURE__*/React.createElement("div", {
    className: classes.startDate
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    color: "disabled"
  }, t('subscriptions.subscription_start_date'), ":"), /*#__PURE__*/React.createElement(Chip, {
    label: dateToFormat(activeAccountSubscription === null || activeAccountSubscription === void 0 ? void 0 : activeAccountSubscription.startDate),
    size: "small",
    variant: "inactive"
  }))), subscriptions.flatMap(subscription => subscription.items.map(item => {
    var _item$fixedPrice;
    return /*#__PURE__*/React.createElement("div", {
      key: item.priceId,
      className: classes.lgSection
    }, /*#__PURE__*/React.createElement(Paper, null, /*#__PURE__*/React.createElement(BackdropLoader, {
      isOpen: isLoading
    }), /*#__PURE__*/React.createElement(DefaultSubscriptionUnit, {
      amount: (_item$fixedPrice = item.fixedPrice) !== null && _item$fixedPrice !== void 0 ? _item$fixedPrice : item.currentUnitPrice,
      count: item.currentQuantity,
      name: item.productName
    })));
  })), !store.account.featureFlagsStore.getIsStripeInvoicingApiEnabled && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('subscriptions.discounts_and_promotions'))), store.payment.coupons.length ? /*#__PURE__*/React.createElement(Paper, null, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading
  }), store.payment.coupons.map(coupon => /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.subSection, " ").concat(classes.tabletSubsection),
    id: coupon.id
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.promocode
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('subscriptions.active_promocode'), ": ", coupon.stripeCouponId), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, coupon.effectiveToDateTimeUtc && coupon.appliedToProducts && t('subscriptions.valid_till_and_products', {
    effectiveToDateTimeUtc: moment(coupon.effectiveToDateTimeUtc).format('MMMM DD, YYYY'),
    appliedOn: coupon.appliedToProducts.join(', ')
  }), coupon.effectiveToDateTimeUtc && coupon.appliedToProducts === null && t('subscriptions.valid_till', {
    effectiveToDateTimeUtc: moment(coupon.effectiveToDateTimeUtc).format('MMMM DD, YYYY')
  }))), /*#__PURE__*/React.createElement(Typography, {
    variant: "h4",
    color: "primary"
  }, "".concat(t('subscriptions.discount'), " ").concat(coupon.amountType === AMOUNT_TYPE.fixedAmount ? "$".concat(Number(coupon.amount).toFixed(2)) : "".concat(Number(coupon.amount).toFixed(2), "%")))))) : null, /*#__PURE__*/React.createElement("form", {
    onSubmit: formik.handleSubmit
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.couponSection, " ").concat(classes.subSection, " ").concat(classes.tabletSubsection)
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('subscriptions.code_for_discount')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('subscriptions.only_one_discount'))), /*#__PURE__*/React.createElement("div", {
    className: classes.codeFieldWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.codeField
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    id: "coupon",
    label: t('subscriptions.enter_code_here'),
    onBlur: formik.handleBlur,
    onChange: formik.handleChange,
    value: formik.values.coupon,
    variant: "outlined"
  })), /*#__PURE__*/React.createElement(Button, {
    disabled: !formik.isValid || !Object.keys(formik.touched).length && !formik.values.coupon.length || store.payment.applyCouponAsync.inProgress,
    type: "submit",
    variant: "contained",
    'custom-attribute': '713-payments-page-sub-tab'
  }, t('buttons.apply'))))))), !!store.stripe.subscriptionCompanyPaymentSummary.nextInvoiceDate && /*#__PURE__*/React.createElement(Paper, {
    variant: "evenWrapper",
    className: classes.paymentPaper
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('subscriptions.payment_summary')), /*#__PURE__*/React.createElement("div", {
    className: classes.lgSection
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.subSection
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.paymentDescription
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('subscriptions.next_monthly_payment')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('subscriptions.automatically_renewed', {
    date: moment(store.stripe.subscriptionCompanyPaymentSummary.nextInvoiceDate).format('MMMM DD, YYYY')
  }))), /*#__PURE__*/React.createElement(Typography, {
    variant: "h2"
  }, "$", store.stripe.subscriptionCompanyPaymentSummary.nextInvoiceAmount)))), /*#__PURE__*/React.createElement("div", {
    className: classes.xlSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('subscriptions.default_payment_method'))), /*#__PURE__*/React.createElement("div", {
    className: classes.smSection
  }, /*#__PURE__*/React.createElement(Paper, null, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading
  }), /*#__PURE__*/React.createElement(PaymentMethod, {
    changeDefaultEnabled: true,
    method: {
      brand: (_store$payment$autopa = store.payment.autopayMethod) === null || _store$payment$autopa === void 0 ? void 0 : _store$payment$autopa.brand,
      last4: (_store$payment$autopa2 = store.payment.autopayMethod) === null || _store$payment$autopa2 === void 0 ? void 0 : _store$payment$autopa2.last4
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.cancelSubscriptionContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cancelSubscription
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('subscriptions.modify_your_subscription')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('subscriptions.modify_your_subscription_description')), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    href: "https://2gvnn.share.hsforms.com/2JsvSJiB9QAe1S6V8ddu2YA",
    target: "_blank"
  }, t('subscriptions.modify_your_subscription_button'))))));
});
export default SubscriptionTab;